import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { Mail, Send, TriangleAlert, Users } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatNumber, hasPermission, METER_READER_MAIL_KIND_LABELS, METER_READER_MODE_LABELS, readerMailSchema } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  applyProblemToForm,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  FileDropzone,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRootError,
  HtmlEditor,
  InputField,
  Label,
  PageHeader,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ToggleGroup,
  toast,
} from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { useListState } from '../../lib/list-search.js';
import { yearFrom, yearOptions } from './utils.js';

const MODE_OPTIONS = Object.entries(METER_READER_MODE_LABELS).map(([value, label]) => ({ value, label }));
const KIND_OPTIONS = Object.entries(METER_READER_MAIL_KIND_LABELS);

/**
 * Offene Ablesungen gruppiert nach Ableser (`meter.reader`). Reine Anzeige-Gruppierung der API-Liste
 * (max. 500 offene Ablesungen je Jahr, siehe admin.md §5 „Lücken“).
 * @param {any[]} readings
 */
function groupByReader(readings) {
  /** @type {Map<string, { reader: { id: number, name: string }|null, readings: any[] }>} */
  const groups = new Map();
  for (const reading of readings) {
    const reader = reading.meter?.reader ?? null;
    const key = reader ? String(reader.id) : 'none';
    if (!groups.has(key)) groups.set(key, { reader, readings: [] });
    groups.get(key).readings.push(reading);
  }
  return [...groups.values()].sort((a, b) => {
    if (!a.reader) return 1;
    if (!b.reader) return -1;
    return a.reader.name.localeCompare(b.reader.name, 'de');
  });
}

/** Zähler › Ableser (F-907, F-909): offene Zähler je Ableser, Erfassungsart, Einzel- und Serienmail. */
export function MeterReadersPage() {
  const api = useApi();
  const { data: me } = useMe();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const year = yearFrom(search.year);
  const query = useQuery(adminQueries.meterReadings(api, year, { per_page: 500, filter: { status: 'open' } }));
  const canManage = hasPermission(me, 'meter-readings.manage');
  const [mailReader, setMailReader] = useState(/** @type {any} */ (null));
  const groups = groupByReader(query.data?.data ?? []);
  const total = query.data?.meta?.total ?? 0;
  const loaded = query.data?.data?.length ?? 0;

  return (
    <>
      <PageHeader title="Ableser" description={`Offene Zähler ${year} je Ableser – Erfassungsart (App/manuell) und Anschreiben.`} />
      <FilterBar>
        <FilterSelect label="Jahr" value={String(year)} onChange={(v) => list.setFilter('year', v)} options={yearOptions(year)} allLabel="Aktuelles Jahr" />
      </FilterBar>
      {canManage && <SeriesMailCard key={year} year={year} />}
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : groups.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Keine offenen Zähler"
          description={`Für ${year} sind keine Ablesungen mehr offen (oder das Jahr ist noch nicht angelegt).`}
        />
      ) : (
        <>
          {total > loaded && (
            <Alert variant="warning" className="mb-3">
              <TriangleAlert aria-hidden="true" />
              <AlertDescription>
                Es werden die ersten {formatNumber(loaded)} von {formatNumber(total)} offenen Ablesungen berücksichtigt.
              </AlertDescription>
            </Alert>
          )}
          <ul aria-label="Ableser" className="flex flex-col divide-y rounded-lg border bg-card">
            {groups.map((group) => (
              <ReaderRow
                key={group.reader ? group.reader.id : 'none'}
                group={group}
                year={year}
                canManage={canManage}
                onMail={() => setMailReader(group.reader)}
              />
            ))}
          </ul>
        </>
      )}
      <ReaderMailDialog year={year} reader={mailReader} onOpenChange={(open) => !open && setMailReader(null)} />
    </>
  );
}

function ReaderRow({ group, year, canManage, onMail }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const { reader, readings } = group;
  const mode = readings.length > 0 && readings.every((r) => r.manual) ? 'manual' : 'app';
  const properties = [...new Set(readings.map((r) => r.meter?.property?.name).filter(Boolean))];

  async function changeMode(next) {
    setPending(true);
    try {
      const result = await api.admin.meterYears.readerMode(year, reader.id, next === 'manual');
      await queryClient.invalidateQueries({ queryKey: keys.admin.resource('meter-readings') });
      toast.success(`${reader.name}: ${METER_READER_MODE_LABELS[next]} (${formatNumber(result?.updated ?? 0)} Ablesungen)`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Erfassungsart konnte nicht geändert werden.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <li aria-label={reader ? reader.name : 'Ohne Ableser'} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-medium">{reader ? reader.name : 'Ohne Ableser'}</span>
        <span className="text-xs text-muted-foreground">{properties.join(', ')}</span>
      </span>
      <Badge variant="warning">{formatNumber(readings.length)} offen</Badge>
      {reader && canManage && (
        <>
          <ToggleGroup label={`Erfassungsart ${reader.name}`} value={mode} onValueChange={(v) => !pending && changeMode(v)} options={MODE_OPTIONS} />
          <Button size="sm" variant="outline" onClick={onMail}>
            <Mail aria-hidden="true" /> Einzelmail
          </Button>
        </>
      )}
      {reader && !canManage && <Badge variant="neutral">{METER_READER_MODE_LABELS[mode]}</Badge>}
    </li>
  );
}

/* ------------------------------------------------------------------ Serienmail */

/** Serienmail an alle Ableser mit offenen Zählern: immer zuerst Probelauf (Bericht), dann ausdrücklich senden. */
function SeriesMailCard({ year }) {
  const api = useApi();
  const [kind, setKind] = useState('first');
  const [report, setReport] = useState(/** @type {{ kind: string, data: any[], meta: any }|null} */ (null));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const current = report?.kind === kind ? report : null;
  const withEmail = current ? current.data.filter((r) => r.email).length : 0;

  async function dryRun() {
    setPending(true);
    setError(null);
    try {
      const body = await api.admin.meterYears.readerMails(year, { kind, dry_run: true });
      setReport({ kind, data: body?.data ?? [], meta: body?.meta ?? {} });
    } catch (e) {
      setError(getErrorMessage(e, 'Der Probelauf ist fehlgeschlagen.'));
    } finally {
      setPending(false);
    }
  }

  async function send() {
    try {
      const body = await api.admin.meterYears.readerMails(year, { kind, dry_run: false });
      const sent = (body?.data ?? []).filter((r) => r.sent).length;
      toast.success(`${METER_READER_MAIL_KIND_LABELS[kind]} an ${formatNumber(sent)} Ableser versendet`);
      setReport(null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Versand ist fehlgeschlagen.'));
    }
  }

  return (
    <Card className="mb-3">
      <CardHeader>
        <CardTitle as="h2">Serienmail an Ableser</CardTitle>
        <CardDescription>
          An alle Ableser mit offenen Zählern {year}. Zuerst ein Probelauf zeigt Empfänger und fehlende E-Mail-Adressen – versendet wird erst danach.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="reader-mail-kind" className="text-xs text-muted-foreground">
              Art
            </Label>
            <Select id="reader-mail-kind" size="sm" value={kind} onChange={(e) => setKind(e.target.value)} wrapperClassName="sm:w-48">
              {KIND_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <Button size="sm" variant="outline" onClick={dryRun} loading={pending}>
            Probelauf
          </Button>
          {current && (
            <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={withEmail === 0}>
              <Send aria-hidden="true" /> Jetzt senden
            </Button>
          )}
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {current && (
          <div className="flex flex-col gap-2">
            {current.meta?.missing_email > 0 && (
              <Alert variant="warning">
                <TriangleAlert aria-hidden="true" />
                <div className="flex flex-col gap-1">
                  <AlertTitle>Fehlende E-Mail-Adressen</AlertTitle>
                  <AlertDescription>
                    {formatNumber(current.meta.missing_email)} Ableser haben keine E-Mail-Adresse und erhalten keine Nachricht.
                  </AlertDescription>
                </div>
              </Alert>
            )}
            <Table aria-label="Probelauf Serienmail" containerClassName="rounded-md border">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead data-numeric="true">Offene Zähler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {current.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      Keine Ableser mit offenen Zählern.
                    </TableCell>
                  </TableRow>
                ) : (
                  current.data.map((row) => (
                    <TableRow key={row.party_id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.email ?? <Badge variant="warning">keine E-Mail</Badge>}</TableCell>
                      <TableCell data-numeric="true">{formatNumber(row.open)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`${METER_READER_MAIL_KIND_LABELS[kind]} jetzt senden?`}
        description="Die E-Mails werden an alle Ableser mit offenen Zählern und hinterlegter Adresse versendet."
        affectedCount={withEmail}
        confirmLabel="Senden"
        onConfirm={send}
      />
    </Card>
  );
}

/* ------------------------------------------------------------------ Einzelmail */

function ReaderMailDialog({ year, reader, onOpenChange }) {
  return (
    <Dialog open={Boolean(reader)} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">{reader && <ReaderMailBody year={year} reader={reader} onClose={() => onOpenChange(false)} />}</DialogContent>
    </Dialog>
  );
}

function ReaderMailBody({ year, reader, onClose }) {
  const api = useApi();
  const draft = useQuery(adminQueries.meterReaderMailDraft(api, year, reader.id));
  return (
    <>
      <DialogHeader>
        <DialogTitle>E-Mail an {reader.name}</DialogTitle>
        <DialogDescription>Vorbelegt mit der Liste der offenen Zähler {year}. Der Empfänger ist die hinterlegte Adresse des Ablesers.</DialogDescription>
      </DialogHeader>
      {draft.error ? (
        <ErrorState compact error={draft.error} onRetry={() => draft.refetch()} />
      ) : draft.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <ReaderMailForm year={year} reader={reader} draft={draft.data} onClose={onClose} />
      )}
    </>
  );
}

function ReaderMailForm({ year, reader, draft, onClose }) {
  const api = useApi();
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const form = useForm({ resolver: zodResolver(readerMailSchema), defaultValues: { subject: draft?.subject ?? '', body_html: draft?.body_html ?? '' } });
  const to = draft?.to ?? null;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const result = await api.admin.meterYears.sendReaderMail(year, reader.id, { subject: values.subject.trim(), body_html: values.body_html, files });
      toast.success(`E-Mail an ${result?.sent_to ?? to} versendet`);
      onClose();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['subject', 'body_html'] });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
        <FormRootError />
        <p className="text-sm">
          <span className="text-muted-foreground">An: </span>
          {to ? <span className="font-medium">{to}</span> : <Badge variant="warning">keine E-Mail-Adresse hinterlegt</Badge>}
        </p>
        {!to && (
          <Alert variant="warning">
            <AlertDescription>Für diesen Ableser ist keine E-Mail-Adresse hinterlegt – die E-Mail kann nicht versendet werden.</AlertDescription>
          </Alert>
        )}
        <InputField name="subject" label="Betreff" autoComplete="off" />
        <FormField
          name="body_html"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Text</FormLabel>
              <FormControl>
                <HtmlEditor label="Text" value={field.value} onChange={field.onChange} onBlur={field.onBlur} rows={10} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FileDropzone files={files} onFilesChange={setFiles} label="Anhänge" maxFiles={3} hint="bis zu 3 Dateien, je max. 20 MB" />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting} disabled={!to}>
            <Send aria-hidden="true" /> E-Mail senden
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
